import { useState } from "react";
import { ArrowRight, GitBranch, Ban } from "lucide-react";
import { TASK_STATUS_ICON } from "./constants";

export const ProjectDependencies = ({ tasks, dependencyEdges, onSaveDeps }) => {
  const [editing, setEditing] = useState(null);
  const [selection, setSelection] = useState([]);

  return (
    <div className="p-6 space-y-4" data-testid="dependencies-tab">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="text-sm font-bold text-foreground">Task Dependencies</h3>
          <p className="text-[11px] text-foreground/50 mt-0.5">Define which tasks must complete before others can start</p>
        </div>
      </div>

      {(dependencyEdges || []).length > 0 && (
        <div className="bg-white border border-border/50 rounded-xl p-4 mb-4">
          <h4 className="text-xs font-bold text-foreground/50 uppercase tracking-wider mb-3">Dependency Chain</h4>
          <div className="flex items-center gap-2 flex-wrap">
            {(dependencyEdges || [])?.map((e, i) => {
              const fromTask = tasks.find(t => t.id === e.from);
              const toTask = tasks.find(t => t.id === e.to);
              return (
                <div key={i} className="flex items-center gap-1.5 text-[10px]" data-testid={`dep-edge-${i}`}>
                  <span className="px-2 py-1 rounded-lg bg-foreground/[0.04] font-semibold text-foreground/60 truncate max-w-[120px]">{fromTask?.title || "?"}</span>
                  <ArrowRight className="w-3 h-3 text-[#014d7d]" />
                  <span className="px-2 py-1 rounded-lg bg-foreground/[0.04] font-semibold text-foreground/60 truncate max-w-[120px]">{toTask?.title || "?"}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tasks?.map(t => {
        const deps = t.depends_on || [];
        const isEditing = editing === t.id;
        const blockedBy = deps?.filter(depId => {
          const depTask = tasks.find(tt => tt.id === depId);
          return depTask && !["completed", "done", "published"].includes(depTask.status);
        });
        return (
          <div key={t.id} className="bg-white border border-border/50 rounded-xl px-4 py-3" data-testid={`dep-task-${t.id}`}>
            <div className="flex items-center gap-3">
              {TASK_STATUS_ICON[t.status] || TASK_STATUS_ICON.todo}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">{t.title}</p>
                <div className="flex items-center gap-2 mt-0.5 text-[10px] text-foreground/50">
                  {t.stage && <span className="px-1.5 py-0.5 bg-foreground/[0.04] rounded capitalize">{t.stage}</span>}
                  {deps.length > 0 && <span className="flex items-center gap-0.5 text-[#014d7d]"><GitBranch className="w-2.5 h-2.5" />{deps.length} dep{deps.length > 1 ? "s" : ""}</span>}
                  {blockedBy.length > 0 && <span className="flex items-center gap-0.5 text-red-500"><Ban className="w-2.5 h-2.5" />Blocked</span>}
                </div>
              </div>
              <button onClick={() => { setEditing(isEditing ? null : t.id); setSelection(deps); }}
                className={`text-[10px] font-semibold px-2.5 py-1 rounded-lg border ${isEditing ? "bg-[#014d7d]/5 border-[#014d7d]/20 text-[#014d7d]" : "border-border/40 text-foreground/40 hover:border-foreground/20"}`}
                data-testid={`edit-deps-${t.id}`}>
                {isEditing ? "Done" : "Edit Deps"}
              </button>
            </div>
            {isEditing && (
              <div className="mt-3 pt-3 border-t border-border/30">
                <p className="text-[10px] font-bold text-foreground/45 uppercase tracking-wider mb-2">Must complete before this task:</p>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {tasks?.filter(other => other.id !== t.id)?.map(other => {
                    const isSelected = selection.includes(other.id);
                    return (
                      <button key={other.id}
                        onClick={() => setSelection(prev => isSelected ? prev?.filter(d => d !== other.id) : [...prev, other.id])}
                        className={`text-[10px] font-medium px-2.5 py-1 rounded-lg border-2 transition-all ${isSelected ? "border-[#014d7d] bg-[#014d7d]/[0.04] text-[#014d7d]" : "border-border/30 text-foreground/40 hover:border-foreground/20"}`}
                        data-testid={`dep-option-${other.id}`}>
                        {other.title}
                      </button>
                    );
                  })}
                </div>
                <button onClick={() => { onSaveDeps(t.id, selection); setEditing(null); }}
                  className="text-[10px] font-semibold px-3 py-1 rounded-lg bg-[#014d7d] text-white" data-testid={`save-deps-${t.id}`}>
                  Save Dependencies
                </button>
              </div>
            )}
          </div>
        );
      })}
      {tasks.length === 0 && <p className="text-center text-foreground/45 py-8 text-sm">No tasks to show dependencies for.</p>}
    </div>
  );
};
